import {
  resolveCategoryColor,
  toPaletteColorName,
} from '@/features/colors/lib/paletteLookup';
import { useUpdateEventMetadata } from '@/features/eventMetadata/hooks/useUpdateEventMetadata';
import { getDisplayEmoji } from '@/features/events/lib/eventEmoji.lib';
import {
  isInstanceHidden,
  setInstanceHidden,
} from '@/features/events/lib/eventVisibility.lib';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useTimeFormat } from '@/features/experiences/hooks/useTimeFormat';
import { softTint } from '@/features/experiences/lib/tint';
import type { SourceEventInstance } from '@/features/sources/lib/sourceEventInstance.lib';
import { useOptimisticValue } from '@/hooks/useOptimisticValue';
import {
  ExperienceColorMode,
  type ExperienceFeed,
} from '@/types/gql.generated';
import { Box, Button, Flex, Icon, Text, Tooltip } from '@/ui';
import { formatDateRangeParts } from '@/utils/formatDate';
import { formatTimeRange } from '@/utils/formatTime';
import { UNCATEGORIZED_FEED_COLOR } from './feedColors';

type Props = {
  feed: ExperienceFeed;
  instance: SourceEventInstance;
};

export const SourceEventRow = ({ feed, instance }: Props) => {
  const { event, isRecurring, startDate, endDate } = instance;
  const { experience } = usePageExperienceContext();
  const timeFormat = useTimeFormat();
  const { updateEventMetadata } = useUpdateEventMetadata({
    experienceId: experience.id,
    eventId: event.id,
  });
  // The row previews what the page renders, so it resolves the emoji the same
  // way — this feed's default over the event's own, a per-event override over both.
  const emoji = getDisplayEmoji(experience, event);
  // Source rows always use the light scheme of the category's color-set,
  // regardless of the experience's colorMode; feeds with no category fall back
  // to the same neutral gray wash the preview card uses.
  const rowBg = feed.category
    ? resolveCategoryColor(
        toPaletteColorName(feed.category.colorName),
        experience.colorPalette,
        ExperienceColorMode.Light
      )
    : softTint(UNCATEGORIZED_FEED_COLOR);

  // Optimistic local flag so the stripe + eye icon flip immediately; it re-syncs
  // once @normy updates the event's metadata from the mutation response.
  const [hidden, setHidden] = useOptimisticValue(
    isInstanceHidden(event, startDate)
  );

  const handleToggleHide = () => {
    const nextHidden = !hidden;
    setHidden(nextHidden);
    // Derive the mutation payload from the same optimistic target so the request
    // and the UI never diverge. Restore the flag if the write fails (the hook
    // rolls back its own cache) to keep the stripe/eye icon in sync.
    updateEventMetadata(
      {
        ...event.metadata,
        hidden: setInstanceHidden(event, startDate, nextHidden),
      },
      { onError: () => setHidden(hidden) }
    );
  };

  // Year included: a source's rows can span years, and a recurring series
  // reads as its own next occurrence rather than the year it started.
  const [startPart, endPart] = formatDateRangeParts(instance, {
    includeYear: true,
  });

  const timeLabel = event.isAllDay
    ? 'All day'
    : formatTimeRange(startDate, endDate, timeFormat);

  // Hiding acts on the one occurrence the row displays, leaving the rest of the
  // series alone — the same as a Schedule sources row, so a recurring row says
  // which of the two it means.
  const hideLabel = isRecurring
    ? `${hidden ? 'Unhide' : 'Hide'} this occurrence`
    : `${hidden ? 'Unhide' : 'Hide'} event`;

  return (
    <Box
      bg={hidden ? undefined : rowBg}
      borderRadius="ah-xl"
      data-group
      display="grid"
      gap={4}
      gridTemplateColumns="155px 1fr 150px 40px"
      layerStyle={hidden ? 'ahHidden' : undefined}
      pos="relative"
      px={4}
      py={4}
    >
      {/* An occurrence spanning days wraps after the dash when the column
          can't fit both ends. */}
      <Text color="ink.strong" fontSize="ah-control" fontWeight={700}>
        <Text as="span" whiteSpace="nowrap">
          {startPart}
        </Text>
        {endPart && (
          <>
            {' '}
            <Text as="span" whiteSpace="nowrap">
              {endPart}
            </Text>
          </>
        )}
      </Text>
      <Flex align="baseline" gap={2} minW={0} overflow="hidden">
        {emoji && (
          <Text flexShrink={0} fontSize="sm">
            {emoji}
          </Text>
        )}
        <Text
          color="ink.strong"
          fontSize="ah-control"
          fontWeight={600}
          letterSpacing="-0.01em"
          noOfLines={1}
        >
          {event.title}
        </Text>
      </Flex>
      {/* Top-aligned so it stays on the title's line when the date wraps. */}
      <Flex align="center" alignSelf="start" gap={1.5} minW={0}>
        <Text
          color="ink.soft"
          fontSize="ah-control"
          fontWeight={600}
          noOfLines={1}
        >
          {timeLabel}
        </Text>
        {isRecurring && (
          <Tooltip label="Repeats">
            <Flex align="center" flexShrink={0}>
              <Icon
                aria-label="Repeats"
                color="ink.soft"
                icon="Repeat"
                role="img"
                size={3.5}
              />
            </Flex>
          </Tooltip>
        )}
      </Flex>
      <Flex align="center" gap={3} justify="flex-end">
        {event.locationWithPlace?.name && (
          <Icon color="ink.soft" icon="MapPin" size={3.5} />
        )}
        {/* Lightning bolt marks a feed event (see EventOrigin). */}
        <Icon color="ink.soft" icon="Zap" size={3.5} />
      </Flex>

      {/* Hover/focus-revealed hide/unhide toggle, floated in the right margin
          outside the row card (matches the Schedule sources row).
          _focusVisible keeps it reachable for keyboard users. */}
      <Button
        _focusVisible={{ opacity: 1 }}
        _groupHover={{ opacity: 1 }}
        aria-label={hideLabel}
        h="32px"
        minW="32px"
        opacity={0}
        pos="absolute"
        px={0}
        right="-44px"
        top="50%"
        transform="translateY(-50%)"
        variant="ahOutlineSubtle"
        onClick={handleToggleHide}
      >
        <Icon icon={hidden ? 'Eye' : 'EyeOff'} size={4} />
      </Button>
    </Box>
  );
};
